import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { projectCases, CATEGORY_LABEL, type ProjectCase, type ProjectCategory } from '@/data/projectCases';
import './ProjectCases.css';

// 图标：轻量 SVG
const Icon = {
  Close: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
  ),
  Arrow: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
  ),
  Building: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M8 10h.01"/><path d="M16 10h.01"/><path d="M8 14h.01"/><path d="M16 14h.01"/></svg>
  ),
  Cal: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
  ),
  User: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  ),
  Star: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
  ),
  Target: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
  ),
};

const ProjectCard: React.FC<{
  project: ProjectCase;
  onOpen: (p: ProjectCase) => void;
  index: number;
}> = ({ project, onOpen, index }) => {
  return (
    <article
      className={`pc-card pc-cat-card--${project.category} ${project.highlight ? 'pc-card--highlight' : ''}`}
      onClick={() => onOpen(project)}
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid="project-card"
    >
      {/* 顶部一行：分类徽章 + 年份 */}
      <div className="pc-card-top">
        <div className={`pc-card-category-badge pc-cat--${project.category}`}>
          <span>{
            project.category === 'ai' ? 'AI'
            : project.category === 'cloud' ? '云计算'
            : '安全'
          }</span>
        </div>
        <div className="pc-card-year">{project.year}</div>
      </div>

      {/* 标题 */}
      <h3 className="pc-card-title">{project.title}</h3>

      {/* 客户行：单行简洁 */}
      <div className="pc-card-line pc-card-customer">
        <Icon.Building />
        <span className="pc-card-line-text pc-card-line-text--single">
          {project.customer}
        </span>
      </div>

      {/* 规模/亮点行：多行，重点信息不截断 */}
      <div className="pc-card-line pc-card-scale">
        <Icon.Target />
        <span className="pc-card-line-text">
          {project.scale}
        </span>
      </div>

      {/* 底部：3 tag + CTA 同行 */}
      <div className="pc-card-foot">
        <div className="pc-card-tags">
          {project.tags.slice(0, 3).map((t, i) => (
            <span key={i} className="pc-tag">{t}</span>
          ))}
        </div>
        <div className="pc-card-cta">
          <span>查看</span>
          <Icon.Arrow />
        </div>
      </div>
    </article>
  );
};

const ProjectModal: React.FC<{
  project: ProjectCase;
  onClose: () => void;
}> = ({ project, onClose }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div className="pc-modal-backdrop" onClick={onClose}>
      <div className="pc-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pc-modal-close" onClick={onClose} aria-label="关闭">
          <Icon.Close />
        </button>

        {/* Header - 恢复完整版：徽章 + 标题 + meta + 规模 + 场景/产品线 */}
        <header className="pc-modal-header">
          <div className={`pc-modal-badge pc-cat--${project.category}`}>
            <span>{
              project.category === 'ai' ? 'AI'
              : project.category === 'cloud' ? '云计算'
              : '安全'
            }</span>
          </div>
          <h2 className="pc-modal-title">{project.title}</h2>

          <div className="pc-modal-meta">
            <div className="pc-modal-meta-item">
              <Icon.Building />
              <span>{project.customer}</span>
            </div>
            <div className="pc-modal-meta-item">
              <Icon.Cal />
              <span>{project.year}</span>
            </div>
            <div className="pc-modal-meta-item">
              <Icon.User />
              <span>{project.role}</span>
            </div>
          </div>

          <div className="pc-modal-scale">{project.scale}</div>

          <div className="pc-modal-tags">
            <div className="pc-modal-tag-group">
              <span className="pc-modal-tag-label">关键词</span>
              {[...project.tags, ...project.productLine].map((t, i) => (
                <span key={i} className="pc-tag">{t}</span>
              ))}
            </div>
          </div>
        </header>

        {/* Body：PPT 原图 + 我的贡献 */}
        <div className="pc-modal-body">
          <div className="pc-slide-wrap">
            <a
              href={project.slide}
              target="_blank"
              rel="noopener noreferrer"
              className="pc-slide-link"
              title="点击查看原图"
            >
              <img
                src={project.slide}
                alt={project.title}
                className="pc-slide-image"
                loading="lazy"
              />
              <div className="pc-slide-hint">
                <span>点击查看原图</span>
              </div>
            </a>
          </div>

          <Section num="01" title="我的贡献">
            <p className="pc-modal-contribution">{project.myContribution}</p>
          </Section>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Section: React.FC<{ num: string; title: string; children: React.ReactNode }> = ({ num, title, children }) => (
  <section className="pc-section">
    <header className="pc-section-header">
      <span className="pc-section-num">{num}</span>
      <h4 className="pc-section-title">{title}</h4>
    </header>
    <div className="pc-section-content">{children}</div>
  </section>
);

const CATEGORIES: Array<ProjectCategory | 'all'> = ['all', 'ai', 'cloud', 'security'];

const ProjectCases: React.FC = () => {
  const [openId, setOpenId] = useState<number | null>(null);
  const [filter, setFilter] = useState<ProjectCategory | 'all'>('all');

  const filtered = filter === 'all'
    ? projectCases
    : projectCases.filter(p => p.category === filter);

  const openProject = filtered.find(p => p.id === openId) ?? null;

  const countOf = (cat: ProjectCategory | 'all') =>
    cat === 'all' ? projectCases.length : projectCases.filter(p => p.category === cat).length;

  const labelOf = (cat: ProjectCategory | 'all') =>
    cat === 'all' ? '全部' : CATEGORY_LABEL[cat];

  return (
    <section className="pc-section-wrap">
      <div className="pc-bg-glow pc-bg-glow-a" aria-hidden />
      <div className="pc-bg-glow pc-bg-glow-b" aria-hidden />

      <div className="pc-container">
        {/* Header — 恢复居中三段式：eyebrow / title / subtitle / toolbar */}
        <div className="pc-header">
          <div className="pc-eyebrow">
            <span className="pc-eyebrow-dot" />
            <span>PROJECT CASES</span>
          </div>
          <h2 className="pc-title">项目案例</h2>
          <p className="pc-subtitle">
            精选 <span className="pc-highlight">{projectCases.length}</span> 个代表性 KA 客户项目 ·
            涵盖 <span className="pc-highlight">桌面云 / 私有云 / 分布式云 / AI 平台 / 零信任 / 安全咨询</span> 全产品线
          </p>

          {/* 提示 + 筛选 同行 */}
          <div className="pc-toolbar">
            <div className="pc-hint">
              <i className="fa-solid fa-hand-pointer"></i>
              <span>点击卡片查看项目详情</span>
            </div>
            <div className="pc-filter">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  className={`pc-filter-btn ${filter === cat ? 'active' : ''}`}
                  onClick={() => setFilter(cat)}
                >
                  <span>{labelOf(cat)}</span>
                  <span className="pc-filter-count">{countOf(cat)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Card Grid */}
        <div className="pc-grid">
          {filtered.map((p, i) => (
            <ProjectCard
              key={p.id}
              project={p}
              index={i}
              onOpen={(pj) => setOpenId(pj.id)}
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      {openProject && (
        <ProjectModal project={openProject} onClose={() => setOpenId(null)} />
      )}
    </section>
  );
};

export { ProjectCases };
export default ProjectCases;
